// ── Coachmarks ────────────────────────────────────────────────────────────────
// One-time, just-in-time teaching hints for the few actions that aren't obvious
// to a first-time (or non-technical) user. Each coachmark shows ONCE, in context,
// then never again — unless the user replays it from Help → Tips & Tutorials.
//
// "Seen" state is a localStorage flag FIRST (instant, synchronous — every
// coachSeen() call in this file stays a plain localStorage read, no round trip),
// backed by profiles.ui_prefs.coach_seen as the durable source of truth
// (2026-08-17, bug report: logging out and back in — or a cleared cache, or a
// second device — replayed every tip, since localStorage alone is per-device).
// markCoachSeen() writes both; syncCoachSeenFromProfile() (called once when the
// profile loads, see useAuth.jsx) pulls the remote set into localStorage so a
// returning/new-device login suppresses hints already seen on the account
// without every coachSeen() check needing to be async.
import { safeStorage } from './safeStorage'
import { supabase } from './supabase'

const KEY = (id) => `nextep_coach_${id}`

// The catalogue. `title`/`blurb` power the Help → Tips list; `where` tells the
// user where the hint appears when they replay it.
// NOTE ON UNIFICATION: the sibling `FeatureCoach` component (feature intros for
// Runs, Cliffhanger, etc.) writes the SAME `nextep_coach_<id>` localStorage flag
// this file reads/clears — so listing those ids here makes them replayable from
// Help → Tips with zero change to FeatureCoach. `route` → Help sends you to that
// surface (re-arming re-fires the hint on mount); `where` → route-less hints that
// only re-appear when you next engage the control (a Run, a co-watch).
export const COACHMARKS = [
  // v17 (2026-09-15): the tips describe the deck and the rings, not the clapboard.
  {
    id: 'add-show',
    title: 'Adding a show',
    blurb: 'Tap Search to find any TV show, open it, and tap "Add to my list." Coming from another app? Import your whole history at once from Settings → Import.',
    route: '/search',
  },
  {
    id: 'mark-watched',
    title: 'Marking an episode watched',
    blurb: 'On Home, swipe the card left. On a show\'s page, tap the ring next to an episode, or press a ring and drag down to mark a whole run at once.',
    route: '/',
  },
  {
    id: 'rate-hold',
    title: 'Rating from the deck',
    blurb: 'Tap the Home card. Five reels fan out; tap one, or slide across them and lift on the one you mean. That rates and marks the episode in one go.',
    route: '/',
  },
  {
    id: 'more-hub',
    title: 'Finding the rest of the app',
    blurb: 'The center "More" button opens Discover, Import, Stats, Notifications, History and Help. Games, People and the Aftershow appear there once you turn them on in Settings → Features.',
    where: 'Look for the pointer at the center "More" button in the bottom bar.',
  },
  {
    id: 'where-playing',
    title: 'Where a season is streaming',
    blurb: 'Streaming lists are per season. When the season you\'re on isn\'t on your services, the Home card says where it is — tap that pill, or Where to watch on any show page, for every season and every service, plus a one-tap way to add the service or be told when it lands on yours. No position set yet? Tap the season you\'re on in that list and the card follows.',
    route: '/',
  },
  {
    id: 'list-gestures',
    title: 'My List shortcuts',
    blurb: 'On My List, swipe a show left to remove it, swipe right to line it up for Tonight, or long-press for quick actions.',
    route: '/list',
  },
  {
    id: 'plans',
    title: 'Tonight',
    blurb: 'Line up what you\'ll actually watch tonight, a short, ordered queue separate from your whole list. The moon lights up after 5pm if nothing is planned.',
    route: '/tonight',
  },
  {
    id: 'alerts-swipe',
    title: 'Tidying your alerts',
    blurb: 'Swipe an alert to dismiss it, or use "Clear" to tidy them all at once.',
    route: '/notifications',
  },
  // ── Shelved features (only useful once their toggle is on) ──
  {
    id: 'cliffhanger',
    title: 'Daily Cliffhanger',
    blurb: 'One mystery show a day, name it from the clues. Turn on games in Settings → Features to play.',
    route: '/cliffhanger',
  },
  {
    id: 'cowatch',
    title: 'Watching with a friend',
    blurb: 'With People on, a show\'s ⋯ sheet has Watch Together: invite a friend to the same episode and react live.',
    where: 'Open a show and tap ⋯ → Watch together.',
  },
  {
    id: 'aftershow',
    title: 'The Aftershow',
    blurb: 'Spoiler-safe conversation for every show; posts about an episode stay sealed until you\'ve watched it. Turn it on in Settings → Features.',
    route: '/aftershow',
  },
]

export function coachSeen(id) {
  try { return safeStorage.get(KEY(id)) === '1' } catch { return false }
}

export function markCoachSeen(id) {
  try { safeStorage.set(KEY(id), '1') } catch { /* non-fatal */ }
  // Fire-and-forget: an atomic jsonb merge server-side (see the 2026-08-17
  // migration), not a client read-modify-write, so two tabs dismissing
  // different coachmarks at once can't clobber each other. No-ops (RLS/auth.uid()
  // is null) for a signed-out visitor — coachmarks still work locally for guests,
  // they just don't durably follow an account until one exists.
  try { supabase.rpc('mark_coach_seen', { p_id: id }).then(() => {}, () => {}) } catch { /* non-fatal */ }
}

// Replay: clear the flag so the in-context hint shows again next time the user
// hits that surface. Local-only — deliberately does NOT clear the remote flag;
// replaying a tip for reference doesn't mean the user has never seen it, and a
// later profile sync isn't allowed to un-suppress the ones still marked locally
// (see syncCoachSeenFromProfile: it only ever fills in missing local flags).
export function resetCoach(id) {
  try { safeStorage.remove(KEY(id)) } catch { /* non-fatal */ }
}

// Called once when the profile loads (useAuth.jsx) — pulls every coachmark id
// the account has EVER seen (on any device) into this device's localStorage, so
// a fresh login (new browser, cleared cache, second device) doesn't replay tips
// already dismissed elsewhere. Additive only: never clears a flag, so it can't
// undo a resetCoach() replay-in-progress or race a concurrent markCoachSeen().
export function syncCoachSeenFromProfile(profile) {
  const seen = profile?.ui_prefs?.coach_seen
  if (!seen || typeof seen !== 'object') return
  for (const id of Object.keys(seen)) {
    if (seen[id]) { try { if (safeStorage.get(KEY(id)) !== '1') safeStorage.set(KEY(id), '1') } catch { /* non-fatal */ } }
  }
}
